import { ActivityTrackSample } from "../types/trail";

export interface TcxTrackPoint extends ActivityTrackSample {
  coordinate: [number, number];
}

export interface TcxData {
  path: [number, number][];
  timestamps: string[];
  samples: TcxTrackPoint[];
  date?: string;
  distance: number;
  duration?: number;
  elevationGain: number;
  elevationLoss: number;
  avgSpeed?: number;
  maxSpeed?: number;
  avgPace?: number;
  maxPace?: number;
  avgHeartRate?: number;
  maxHeartRate?: number;
  center: [number, number];
  startPoint: [number, number];
  endPoint: [number, number];
}

const descendants = (element: Element, name: string): Element[] =>
  Array.from(element.getElementsByTagName("*"))
    .filter((candidate) => candidate.localName === name);

const firstDescendant = (element: Element, name: string): Element | undefined =>
  descendants(element, name)[0];

const firstChild = (element: Element, name: string): Element | undefined =>
  Array.from(element.children).find((candidate) => candidate.localName === name);

const numericValue = (element: Element | undefined): number | undefined => {
  if (!element?.textContent) return undefined;
  const value = Number(element.textContent.trim());
  return Number.isFinite(value) ? value : undefined;
};

const valueWithin = (element: Element, parentName: string, valueName: string): number | undefined => {
  const parent = firstDescendant(element, parentName);
  return parent ? numericValue(firstDescendant(parent, valueName)) : undefined;
};

const distanceBetween = (first: [number, number], second: [number, number]): number => {
  const radians = (degrees: number) => degrees * Math.PI / 180;
  const latitudeDelta = radians(second[0] - first[0]);
  const longitudeDelta = radians(second[1] - first[1]);
  const latitude1 = radians(first[0]);
  const latitude2 = radians(second[0]);
  const haversine = Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(latitude1) * Math.cos(latitude2) * Math.sin(longitudeDelta / 2) ** 2;
  return 6371000 * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
};

const sumDefined = (values: (number | undefined)[]): number | undefined => {
  const defined = values.filter((value): value is number => value !== undefined);
  return defined.length ? defined.reduce((sum, value) => sum + value, 0) : undefined;
};

export function summarizeTCXSamples(samples: TcxTrackPoint[]) {
  const distance = samples.slice(1).reduce(
    (total, sample, index) => total + distanceBetween(samples[index].coordinate, sample.coordinate),
    0,
  );
  const timestampSamples = samples.flatMap(({ timestamp }) => timestamp ? [timestamp] : []);
  const duration = timestampSamples.length > 1
    ? Math.max(0, (Date.parse(timestampSamples[timestampSamples.length - 1]) - Date.parse(timestampSamples[0])) / 1000)
    : undefined;
  let elevationGain = 0;
  let elevationLoss = 0;
  for (let index = 1; index < samples.length; index += 1) {
    const previousAltitude = samples[index - 1].elevation;
    const currentAltitude = samples[index].elevation;
    if (previousAltitude === undefined || currentAltitude === undefined) continue;
    const delta = currentAltitude - previousAltitude;
    if (delta > 0) elevationGain += delta;
    else elevationLoss -= delta;
  }

  const heartRates = samples.flatMap(({ heartRate }) => heartRate === undefined ? [] : [heartRate]);
  const recordedSpeeds = samples.flatMap(({ speed }) => speed === undefined ? [] : [speed]);
  const derivedSpeeds = samples.slice(1).flatMap((sample, index) => {
    const previousTimestamp = samples[index].timestamp;
    const elapsed = sample.timestamp && previousTimestamp
      ? (Date.parse(sample.timestamp) - Date.parse(previousTimestamp)) / 1000
      : 0;
    return elapsed > 0 ? [distanceBetween(samples[index].coordinate, sample.coordinate) / elapsed] : [];
  });
  const averageSpeed = duration && distance > 0 ? distance / duration : undefined;
  const paceSamples = recordedSpeeds.length ? recordedSpeeds : derivedSpeeds;

  return {
    distance: Math.round(distance),
    ...(duration !== undefined ? { duration: Math.round(duration) } : {}),
    elevationGain: Math.round(elevationGain),
    elevationLoss: Math.round(elevationLoss),
    ...(averageSpeed !== undefined ? { avgSpeed: Math.round(averageSpeed * 36) / 10 } : {}),
    ...((recordedSpeeds.length || derivedSpeeds.length)
      ? { maxSpeed: Math.round(Math.max(...(recordedSpeeds.length ? recordedSpeeds : derivedSpeeds)) * 36) / 10 }
      : {}),
    ...(averageSpeed !== undefined ? { avgPace: Math.round((1000 / averageSpeed) / 60 * 10) / 10 } : {}),
    ...(paceSamples.length
      ? { maxPace: Math.round((1000 / Math.max(...paceSamples)) / 60 * 10) / 10 }
      : {}),
    ...(heartRates.length
      ? {
          avgHeartRate: Math.round(heartRates.reduce((sum, value) => sum + value, 0) / heartRates.length),
          maxHeartRate: Math.max(...heartRates),
        }
      : {}),
  };
}

export function parseTCXFile(tcxContent: string): TcxData | null {
  try {
    const xml = new DOMParser().parseFromString(tcxContent, "application/xml");
    if (descendants(xml.documentElement, "parsererror").length ||
        xml.documentElement.localName === "parsererror") return null;

    const activity = descendants(xml.documentElement, "Activity")[0];
    if (!activity) return null;

    const laps = descendants(activity, "Lap");
    const lapPoints = laps.map((lap) => descendants(lap, "Trackpoint"));
    const pointElements = lapPoints.length
      ? lapPoints.flat()
      : descendants(activity, "Trackpoint");

    const samples: TcxTrackPoint[] = pointElements.flatMap((trackpoint) => {
      const position = firstDescendant(trackpoint, "Position");
      const latitude = position && numericValue(firstDescendant(position, "LatitudeDegrees"));
      const longitude = position && numericValue(firstDescendant(position, "LongitudeDegrees"));
      if (latitude === undefined || longitude === undefined ||
          latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return [];

      const timestamp = firstDescendant(trackpoint, "Time")?.textContent?.trim();
      const parsedTimestamp = timestamp && !Number.isNaN(Date.parse(timestamp))
        ? new Date(timestamp).toISOString()
        : undefined;
      const extension = firstDescendant(trackpoint, "Extensions");
      const speedElement = extension && firstDescendant(extension, "Speed");
      const pointSpeed = numericValue(speedElement);

      return [{
        coordinate: [latitude, longitude],
        ...(parsedTimestamp ? { timestamp: parsedTimestamp } : {}),
        ...(numericValue(firstDescendant(trackpoint, "AltitudeMeters")) !== undefined
          ? { elevation: numericValue(firstDescendant(trackpoint, "AltitudeMeters")) }
          : {}),
        ...(numericValue(firstDescendant(trackpoint, "DistanceMeters")) !== undefined
          ? { cumulativeDistance: numericValue(firstDescendant(trackpoint, "DistanceMeters")) }
          : {}),
        ...(pointSpeed !== undefined ? { speed: pointSpeed } : {}),
        ...(valueWithin(trackpoint, "HeartRateBpm", "Value") !== undefined
          ? { heartRate: valueWithin(trackpoint, "HeartRateBpm", "Value") }
          : {}),
      }];
    });

    if (samples.length === 0) return null;

    for (let index = 1; index < samples.length; index += 1) {
      const previous = samples[index - 1];
      const current = samples[index];
      if (current.speed === undefined && previous.timestamp && current.timestamp) {
        const elapsed = (Date.parse(current.timestamp) - Date.parse(previous.timestamp)) / 1000;
        if (elapsed > 0) current.speed = distanceBetween(previous.coordinate, current.coordinate) / elapsed;
      }
      if (current.speed !== undefined && current.speed > 0) {
        current.pace = 1000 / current.speed / 60;
      }
    }

    const path = samples.map(({ coordinate }) => coordinate);
    const timestamps = samples.every(({ timestamp }) => timestamp)
      ? samples.map(({ timestamp }) => timestamp as string)
      : [];
    const sampleMetrics = summarizeTCXSamples(samples);
    const lapDistances = laps.map((lap) => numericValue(firstChild(lap, "DistanceMeters")));
    const lapTrackDistances = lapPoints.map((points) => {
      const cumulativeDistance = points
        .map((point) => numericValue(firstDescendant(point, "DistanceMeters")))
        .filter((value): value is number => value !== undefined);
      if (cumulativeDistance.length) return cumulativeDistance[cumulativeDistance.length - 1];
      const coordinates = points.flatMap((point) => {
        const position = firstDescendant(point, "Position");
        const latitude = position && numericValue(firstDescendant(position, "LatitudeDegrees"));
        const longitude = position && numericValue(firstDescendant(position, "LongitudeDegrees"));
        return latitude !== undefined && longitude !== undefined
          ? [[latitude, longitude] as [number, number]]
          : [];
      });
      return coordinates.slice(1).reduce(
        (total, coordinate, index) => total + distanceBetween(coordinates[index], coordinate),
        0,
      );
    });
    const tcxTrackDistance = lapTrackDistances.length
      ? sumDefined(lapTrackDistances)
      : samples[samples.length - 1].cumulativeDistance;
    const distance = sumDefined(lapDistances) ?? tcxTrackDistance ?? sampleMetrics.distance;
    const lapDurations = laps.map((lap) => numericValue(firstDescendant(lap, "TotalTimeSeconds")));
    const duration = sumDefined(lapDurations) ?? sampleMetrics.duration;
    const averageSpeed = duration && distance > 0 ? distance / duration : undefined;
    const speedSamples = samples.flatMap(({ speed }) => speed === undefined ? [] : [speed]);
    const derivedSpeeds = samples.slice(1).flatMap((sample, index) => {
      const previousTimestamp = samples[index].timestamp;
      const elapsed = sample.timestamp && previousTimestamp
        ? (Date.parse(sample.timestamp) - Date.parse(previousTimestamp)) / 1000
        : 0;
      return elapsed > 0
        ? [distanceBetween(samples[index].coordinate, sample.coordinate) / elapsed]
        : [];
    });
    const maximumSpeeds = speedSamples.length ? speedSamples : derivedSpeeds;
    const heartRates = samples.flatMap(({ heartRate }) => heartRate === undefined ? [] : [heartRate]);
    const lapAverageHeartRates = laps.map((lap) => valueWithin(lap, "AverageHeartRateBpm", "Value"));
    const lapAverageCount = lapAverageHeartRates.filter((value) => value !== undefined).length;
    const averageHeartRate = heartRates.length
      ? heartRates.reduce((sum, value) => sum + value, 0) / heartRates.length
      : lapAverageCount
        ? (sumDefined(lapAverageHeartRates) || 0) / lapAverageCount
        : undefined;
    const lapMaximumHeartRates = laps.map((lap) => valueWithin(lap, "MaximumHeartRateBpm", "Value"));
    const maxHeartRate = heartRates.length
      ? Math.max(...heartRates)
      : Math.max(...lapMaximumHeartRates.filter((value): value is number => value !== undefined));
    const center: [number, number] = [
      path.reduce((sum, point) => sum + point[0], 0) / path.length,
      path.reduce((sum, point) => sum + point[1], 0) / path.length,
    ];

    const activityId = firstDescendant(activity, "Id")?.textContent?.trim();
    const firstPointTimestamp = samples.find(({ timestamp }) => timestamp)?.timestamp;
    const activityDate = activityId && !Number.isNaN(Date.parse(activityId))
      ? new Date(activityId).toISOString()
      : firstPointTimestamp;

    return {
      path,
      timestamps,
      samples,
      ...(activityDate ? { date: activityDate } : {}),
      distance: Math.round(distance),
      ...(duration !== undefined ? { duration: Math.round(duration) } : {}),
      elevationGain: sampleMetrics.elevationGain,
      elevationLoss: sampleMetrics.elevationLoss,
      ...(averageSpeed !== undefined ? { avgSpeed: Math.round(averageSpeed * 3.6 * 10) / 10 } : {}),
      ...(maximumSpeeds.length ? { maxSpeed: Math.round(Math.max(...maximumSpeeds) * 3.6 * 10) / 10 } : {}),
      ...(averageSpeed !== undefined ? { avgPace: Math.round((1000 / averageSpeed) / 60 * 10) / 10 } : {}),
      ...(sampleMetrics.maxPace !== undefined ? { maxPace: sampleMetrics.maxPace } : {}),
      ...(averageHeartRate !== undefined && Number.isFinite(averageHeartRate)
        ? { avgHeartRate: Math.round(averageHeartRate) }
        : {}),
      ...(Number.isFinite(maxHeartRate) ? { maxHeartRate: maxHeartRate } : {}),
      center,
      startPoint: path[0],
      endPoint: path[path.length - 1],
    };
  } catch {
    return null;
  }
}
